import { SORTING_ORDER } from "enum/sorting.enum";
import { FIRST_PAGE, PAGE_SIZE } from "helpers/values";
import moment from "moment";
import React, { CSSProperties } from "react";
import { connect } from "react-redux";
import { RouteProps } from "react-router-dom";
import {
  EmailAuditLogsState,
  fetchEmailAuditLogs,
  Notification,
  payloadFetchEmailAuditLogs,
} from "../state/email_audit_log";
import ApiTable from "./ApiTable";
import { State } from "state";
import {
  deliveredStatuses,
  SendGridEventStatus,
  failedStatuses,
  pendingStatuses,
} from "enum/emailStatus.enum";

type EmailAuditLogProps = RouteProps & {
  style?: React.CSSProperties;
  pageName?: string;
  // Redux props
  emailAuditLogs: EmailAuditLogsState;
  fetchEmailAuditLogs: (props: payloadFetchEmailAuditLogs) => Promise<void>;
};

// Styles
const styles = {
  container: { background: "#fff", padding: 10, borderRadius: 10 },
  statusBadge: (status: SendGridEventStatus): CSSProperties => {
    let bgColor = "#e0e0e0";
    let textColor = "#666";
    if (pendingStatuses.includes(status)) {
      bgColor = "#fff3cd";
      textColor = "#856404";
    } else if (deliveredStatuses.includes(status)) {
      bgColor = "#d1e7dd";
      textColor = "#24A148";
    } else if (failedStatuses.includes(status)) {
      bgColor = "#FDF2F3";
      textColor = "#E01F29";
    }

    return {
      padding: "4px 12px",
      borderRadius: 4,
      backgroundColor: bgColor,
      color: textColor,
      fontSize: 11,
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      minWidth: "80px",
      justifyContent: "center",
      textTransform: "capitalize",
    };
  },
  bullet: (color: string) => ({
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: color,
    display: "inline-block",
  }),
};

class EmailAuditLog extends React.Component<EmailAuditLogProps, payloadFetchEmailAuditLogs> {
  constructor(props: EmailAuditLogProps) {
    super(props);
    this.state = {
      size: PAGE_SIZE,
      page: FIRST_PAGE,
      email: "",
      subject: "",
      created_at: "",
      status: "" as SendGridEventStatus,
      sort: "",
    };
  }

  componentDidMount() {
    this.props.fetchEmailAuditLogs({ size: this.state.size, page: this.state.page });
  }

  render() {
    const { style, emailAuditLogs } = this.props;

    const columns = [
      { field: "email", header: "Recipient Email" },
      { field: "subject", header: "Subject" },
      {
        field: "created_at",
        type: "date",
        header: "Published At",
        formatter: (_: any, row: Notification) => {
          return moment(row.created_at).format("MM-DD-YYYY hh:mm A");
        },
      },
      {
        field: "status",
        header: "System Status",
        formatter: (_: any, row: any) => {
          let textColor = "#666";
          if (pendingStatuses.includes(row.status)) textColor = "#856404";
          else if (deliveredStatuses.includes(row.status)) textColor = "#24A148";
          else if (failedStatuses.includes(row.status)) textColor = "#E01F29";

          return row.status ? (
            <span style={styles.statusBadge(row.status)}>
              <span style={styles.bullet(textColor)}></span>
              {row.status}
            </span>
          ) : (
            "-"
          );
        },
      },
    ];

    return (
      <div style={{ ...styles.container, ...style }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                marginBottom: 5,
              }}
            >
              Email Audit Logs
            </h2>
          </div>
        </div>
        <div style={{ margin: "1%" }}>
          <ApiTable
            columns={columns}
            data={emailAuditLogs?.content || []}
            loading={emailAuditLogs?.loading || false}
            page={emailAuditLogs.page}
            pageSize={emailAuditLogs.size}
            total={emailAuditLogs.total}
            pageSizeOptions= {[10, 20, 50, 100, 200]}
            onSearch={(field: string, value: string) => {
              this.setState(
                (prevState) => ({
                  ...prevState,
                  page: FIRST_PAGE,
                  [field]: value,
                  search: value,
                }),
                () => {
                  const { size = PAGE_SIZE, email, subject, created_at, status } = this.state;
                  const params: payloadFetchEmailAuditLogs = {
                    size,
                    page: 1,
                    ...(email && { email }),
                    ...(subject && { subject }),
                    ...(created_at && { created_at }),
                    ...(status && { status }),
                  };

                  this.props.fetchEmailAuditLogs(params).then();
                }
              );
            }}
            onSort={(field: string, order: SORTING_ORDER) => {
              // Build final sort param
              const sortParam = `${field}.${order}`;

              this.setState(
                (prevState) => ({
                  ...prevState,
                  page: FIRST_PAGE, // reset pagination
                }),
                () => {
                  const { size = PAGE_SIZE, email, subject, created_at, status } = this.state;
                  const params: payloadFetchEmailAuditLogs = {
                    size,
                    page: 1,
                    sort: sortParam,
                    ...(email && { email }),
                    ...(subject && { subject }),
                    ...(created_at && { created_at }),
                    ...(status && { status }),
                  };
                  this.setState({ sort: sortParam });
                  this.props.fetchEmailAuditLogs(params).then();
                }
              );
            }}
            onPageChange={(page: number, size: number) => {
              //    const searchFields = {
              //      email: this.state.email,
              //      first_name: this.state.first_name,
              //      last_name: this.state.last_name,
              //      company: this.state.company,
              //      chain_name: this.state.chain_name,
              //      role_name: this.state.role_name,
              //    };

              //    const activeSearchFields: any = {};
              //    Object.entries(searchFields).forEach(([key, value]) => {
              //      if (value !== "" && value !== null && value !== undefined) {
              //        activeSearchFields[key] = value;
              //      }
              //    });

              const params: { size?: number; page?: number } = {
                page,
                size,
              };

              this.props.fetchEmailAuditLogs(params);
              this.setState({ page, size });
            }}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: State) => {
  return {
    emailAuditLogs: state.emailAuditLogs,
  };
};

const mapDispatchToProps = {
  fetchEmailAuditLogs,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailAuditLog);
